import { useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ActionType, HandHygieneAction } from '@/types/opportunity';
import { Button } from '@components/common/Button';

interface ActionSelectorProps {
  onComplete: (action: HandHygieneAction) => void;
  onBack: () => void;
  initialValue?: ActionType;
  healthworkerColor?: string;
}

export function ActionSelector({
  onComplete,
  onBack,
  initialValue,
  healthworkerColor,
}: ActionSelectorProps) {
  const { t } = useTranslation();
  const [actionType, setActionType] = useState<ActionType | null>(
    initialValue || null
  );
  const [showGlovesPrompt, setShowGlovesPrompt] = useState(false);

  const handleActionSelect = (type: ActionType) => {
    setActionType(type);

    if (type === 'missed') {
      setShowGlovesPrompt(true);
    } else {
      const action: HandHygieneAction = {
        actionPerformed: true,
        actionType: type,
        handrubUsed: type === 'handrub' || type === 'both',
        handwashUsed: type === 'handwash' || type === 'both',
      };
      onComplete(action);
    }
  };

  const handleGlovesResponse = (worn: boolean) => {
    const action: HandHygieneAction = {
      actionPerformed: false,
      actionType: 'missed',
      handrubUsed: false,
      handwashUsed: false,
      glovesWorn: worn,
    };
    onComplete(action);
  };

  if (showGlovesPrompt) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('actions.glovesTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('actions.glovesSubtitle')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              onClick={() => handleGlovesResponse(true)}
              sx={{ height: 80 }}
            >
              {t('common.yes')}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="error"
              size="large"
              fullWidth
              onClick={() => handleGlovesResponse(false)}
              sx={{ height: 80 }}
            >
              {t('common.no')}
            </Button>
          </Grid>
        </Grid>

        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            setShowGlovesPrompt(false);
            setActionType(null);
          }}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('actions.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('actions.subtitle')}
      </Typography>

      <Grid container spacing={2}>
        {(['handrub', 'handwash', 'both'] as const).map((type) => (
          <Grid item xs={12} key={type}>
            <Button
              variant={actionType === type ? 'contained' : 'outlined'}
              size="large"
              fullWidth
              onClick={() => handleActionSelect(type)}
              sx={{
                height: 80,
                fontSize: '1.1rem',
                borderLeft: healthworkerColor ? `4px solid ${healthworkerColor}` : undefined,
              }}
            >
              {t(`actions.${type}`)}
            </Button>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button
            variant={actionType === 'missed' ? 'contained' : 'outlined'}
            color="error"
            size="large"
            fullWidth
            onClick={() => handleActionSelect('missed')}
            sx={{
              height: 80,
              fontSize: '1.1rem',
              borderLeft: healthworkerColor ? `4px solid ${healthworkerColor}` : undefined,
            }}
          >
            {t('actions.missed')}
          </Button>
        </Grid>
      </Grid>

      <Button variant="outlined" fullWidth onClick={onBack} sx={{ mt: 2 }}>
        {t('actions.backToIndications')}
      </Button>
    </Box>
  );
}
